"use client";

import { ChevronLeft, ChevronRight } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";

type PageItem = number | "gap-start" | "gap-end";

export function paginationItems(currentPage: number, pageCount: number): PageItem[] {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_page, index) => index + 1);
  }
  const windowStart = Math.max(2, Math.min(currentPage - 1, pageCount - 4));
  const windowEnd = Math.min(pageCount - 1, Math.max(currentPage + 1, 5));
  const middle = Array.from(
    { length: windowEnd - windowStart + 1 },
    (_page, index) => windowStart + index,
  );
  return [
    1,
    ...(windowStart > 2 ? (["gap-start"] as const) : []),
    ...middle,
    ...(windowEnd < pageCount - 1 ? (["gap-end"] as const) : []),
    pageCount,
  ];
}

function PageItemControl({
  item,
  currentPage,
  onPageChange,
}: {
  item: PageItem;
  currentPage: number;
  onPageChange: (page: number) => void;
}) {
  if (typeof item !== "number") {
    return (
      <span className="px-1 text-muted-foreground" aria-hidden>
        …
      </span>
    );
  }
  return (
    <Button
      variant={item === currentPage ? "primary" : "ghost"}
      size="icon"
      aria-label={`Page ${item}`}
      aria-current={item === currentPage ? "page" : undefined}
      onClick={() => onPageChange(item)}
    >
      {item}
    </Button>
  );
}

export function ListingPagination({
  currentPage,
  pageCount,
  onPageChange,
}: {
  currentPage: number;
  pageCount: number;
  onPageChange: (page: number) => void;
}) {
  if (pageCount <= 1) return null;
  return (
    <nav aria-label="Listing pages" className="mt-8 flex items-center justify-center gap-1">
      <IconButton
        size="default"
        aria-label="Previous page"
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        <ChevronLeft aria-hidden />
      </IconButton>
      <span className="px-2 text-sm text-muted-foreground sm:hidden">
        Page {currentPage} of {pageCount}
      </span>
      <div className="hidden items-center gap-1 sm:flex">
        {paginationItems(currentPage, pageCount).map((item) => (
          <PageItemControl key={item} item={item} currentPage={currentPage} onPageChange={onPageChange} />
        ))}
      </div>
      <IconButton
        size="default"
        aria-label="Next page"
        disabled={currentPage === pageCount}
        onClick={() => onPageChange(currentPage + 1)}
      >
        <ChevronRight aria-hidden />
      </IconButton>
    </nav>
  );
}
